import {
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  Box, Grid, Typography,
} from '@mui/material';

import { ImagesContext } from '../../../../contexts/ImagesContext';

// Components
import LimiterInput from './LimiterInput';

// Enums
import { InputLimits } from '../../../../enums/InputLimits';

interface Props {
  title?: string
  description?: string
  keywords?: string
  faviconUrl?: string
  faviconFile?: Blob
  onTitleChange?: (title: string) => void
  onDescriptionChange?: (description: string) => void
  onKeywordsChange?: (keywords: string) => void
  onFaviconFileChange?: (favicon_file: Blob) => void
}

function BasicSetting(
  props: Props,
) {
  const {
    title,
    description,
    keywords,
    faviconUrl,
    faviconFile,
    onTitleChange,
    onDescriptionChange,
    onKeywordsChange,
    onFaviconFileChange,
  } = props;
  const { exchangeForUrl } = useContext(ImagesContext);

  // initial values
  const [titleValue, setTitleValue] = useState<string>(title ?? '');
  const [descriptionValue, setDescriptionValue] = useState<string>(description ?? '');
  const [keywordsValue, setKeywordsValue] = useState<string>(keywords ?? '');

  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>(faviconUrl ?? '/img/icon_gray_64.png');

  useEffect(() => {
    // check if the faviconUrl is a blob url
    if (faviconFile != null) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviewUrl(reader.result as string);
      reader.readAsDataURL(faviconFile);

      return;
    }

    if (faviconUrl == null) return;

    exchangeForUrl(faviconUrl).then((url) => {
      setImagePreviewUrl(url);
    });
  }, [exchangeForUrl, faviconFile, faviconUrl]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const reader = new FileReader();
    const file = e.target.files?.[0];
    const fileReaderForBinary = new FileReader();
    fileReaderForBinary.onload = () => {
      const res = fileReaderForBinary.result as ArrayBuffer;
      const blobData = new Blob([res]);
      onFaviconFileChange(blobData);
    };
    fileReaderForBinary.readAsArrayBuffer(file);

    reader.onloadend = () => {
      setImagePreviewUrl(reader.result as string);
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box display="flex" flexDirection="column" rowGap="10px">
      <Box>
        <h2 className="mb-2">General</h2>
      </Box>
      <Box className="pb-2">
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LimiterInput
              required
              value={titleValue}
              limit={InputLimits.TITLE_LIMIT}
              placeholder="Title"
              label="Title"
              onChange={(newTitle: string) => {
                setTitleValue(newTitle);
                if (onTitleChange) {
                  onTitleChange(newTitle);
                }
              }}
            />

            <Typography variant="caption">
              *Displayed in search engine results.
            </Typography>
          </Grid>
        </Grid>
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LimiterInput
              textarea
              value={descriptionValue}
              limit={InputLimits.DESCRIPTION_LIMIT}
              placeholder="Description"
              label="Description"
              onChange={(newDescription: string) => {
                setDescriptionValue(newDescription);
                if (onDescriptionChange) {
                  onDescriptionChange(newDescription);
                }
              }}
            />

            <Typography variant="caption">
              *A summary of your page for search engines.
            </Typography>
          </Grid>
        </Grid>
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LimiterInput
              textarea
              value={keywordsValue}
              limit={InputLimits.KEYWORDS_LIMIT}
              placeholder="Keywords"
              label="Keywords"
              onChange={(newKeywords: string) => {
                setKeywordsValue(newKeywords);
                if (onKeywordsChange) {
                  onKeywordsChange(newKeywords);
                }
              }}
            />

            <Typography variant="caption">
              *Keywords for search engines, separated by commas.
            </Typography>
          </Grid>
        </Grid>
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" className="text-bold mb-2">
              Favicon
            </Typography>
            <Box className="setting-image-favicon mb-2">
              <img src={imagePreviewUrl} alt="favicon" />
            </Box>
            <input
              type="file"
              id="upload-button"
              className="form-control mb-1"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            <label htmlFor="upload-button" className="btn btn-primary mb-2">
              Upload Image
            </label>
            <br />
            <Typography variant="caption">
              *Icon displayed in browser tabs and bookmarks.
            </Typography>
            <br />
            <Typography variant="caption">
              *Max 5MB. PNG format only.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default BasicSetting;
